import Btn from 'components/buttons/Btn'
import { MoneyInput } from 'components/form'
import PropTypes from 'prop-types'
import { Fragment, useEffect, useMemo } from 'react'
import { ButtonGroup } from 'react-bootstrap'
import Form from 'react-bootstrap/Form'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FMT, platform } from 'utils'

export default function PriceRows({ menuIndex, menu, itemName, singlePrice, wildcard, allowedZero }) {
	const { t }       = useTranslation()
	const fieldPrefix = `menu_prices.${menuIndex}.prices`
	const clickOrTap  = platform.clickOrTap(t)
	const RAW_PRICE   = useMemo(() => ({ display_name: itemName, price_value: singlePrice? undefined : 0, is_shown: true }), [itemName, singlePrice])

  const { control, register, setValue, getValues, watch, formState: { errors } } = useFormContext()

	// We NEED to specify the keyname just because RHF overwrites any ID that the array may have
	const { fields, remove, append } = useFieldArray({ control, name: fieldPrefix, keyName: 'RHF_id' })

	function toggleStatus(priceKey) {
		const key = `${priceKey}.is_shown`
		setValue(key, !getValues(key))
	}

	useEffect(() => {
		if (singlePrice) { //forces a single-priced item to...
			if (fields.length > 1) {
				setValue(fieldPrefix, [fields[0]]) //...keep just the first price
			} else if (fields.length < 1) {
				setValue(fieldPrefix, [{ ...RAW_PRICE, is_shown: false }]) //...have at least one price
			}
		}
	}, [singlePrice, setValue, fields, menuIndex, fieldPrefix, RAW_PRICE])

  return <>
	  <tbody>
	    {fields.map((price, i) => {
	      const priceKey = `${fieldPrefix}.${i}`
		    const currentStatus = watch(`${priceKey}.is_shown`) //causes re-render on status changes, to swap the button
		    const statusContent = currentStatus?
			    { icon: 'eye',       variant: 'success', title: t('Shown; {{clickOrTap}} to hide', { clickOrTap }) } :
		      { icon: 'eye-slash', variant: 'warning', title: t('Hidden; {{clickOrTap}} to show', { clickOrTap }) }

	      return <Fragment key={price.RHF_id}>
	        <tr>
	          {!singlePrice && <td>
	            <Form.Control defaultValue={price.display_name || itemName} {...register(`${priceKey}.display_name`, { maxLength: 90 })} />
	          </td>}

	          <td>
	            <Controller
	              control={control}
	              name={`${priceKey}.text_price`}
	              defaultValue={FMT.money(price.price_value) || ''}
	              rules={{ validate: () => allowedZero || getValues(`${priceKey}.price_value`) !== 0 }}
	              render={({ field }) => (
									<MoneyInput {...field} onChange={v => setValue(`${priceKey}.price_value`, FMT.demoney(v))}
															placeholder={singlePrice? t('Set to show in menu', { context: 'Set a price in this field to show this item in the menu'}) : ''}/>
	              )}
	            />
	          </td>

	          <td className="align-middle text-nowrap">
		          <ButtonGroup>
			          <Btn size="sm" fixedWidth {...statusContent} onClick={() => toggleStatus(priceKey)}/>{' '}
			          <Btn size="sm" fixedWidth variant="danger" icon="trash" onClick={() => remove(i)}/>
		          </ButtonGroup>
	          </td>
	        </tr>

	        {errors?.menu_prices && errors.menu_prices[menuIndex]?.prices[i]?.text_price && <tr>
            <td colSpan={singlePrice? 2 : 3}>
              <Form.Text className="text-danger">{t('The price can\'t be zero.')}</Form.Text>
            </td>
	        </tr>}
	        {errors?.menu_prices && errors.menu_prices[menuIndex]?.prices[i]?.display_name && <tr>
            <td colSpan={singlePrice? 2 : 3}>
              <Form.Text className="text-danger">{t('Price name should be 90 characters max.')}</Form.Text>
            </td>
	        </tr>}
	      </Fragment>
	    })}
	  </tbody>

	  {singlePrice? null : <tfoot>
		  <tr>
			  <td colSpan={3}>
				  <Btn block size="sm" disabled={wildcard} variant="outline-success" swap icon="plus"
				       onClick={() => append(RAW_PRICE)}>
					  {t('Add price')}
				  </Btn>
			  </td>
		  </tr>
	  </tfoot>}
  </>
}

PriceRows.propTypes = {
  menuIndex  : PropTypes.number.isRequired,
  menu       : PropTypes.shape({ id: PropTypes.number, display_name: PropTypes.string }).isRequired,
  itemName   : PropTypes.string.isRequired,
  singlePrice: PropTypes.bool.isRequired,
	allowedZero: PropTypes.bool.isRequired,
}
